/** The priorities a sample may carry, as the Priority pill stores them. */
export const PRIORITY_LABELS: readonly string[] = ['Highest', 'High', 'Medium', 'Low', 'Lowest'];

/** The AG Grid event sources a fill-handle drag and a paste write cells with. */
export const PRIORITY_WRITE_SOURCES: ReadonlySet<string> = new Set(['rangeService', 'paste']);

/** Keys that would open AG Grid's own text editor over the pill, or clear the cell. */
const EDITOR_KEYS: ReadonlySet<string> = new Set(['Enter', 'F2', 'Delete', 'Backspace']);

/** What decides whether a row's Priority may be changed: the pill's own disabled rule. */
export interface PriorityFillGates {
  isSupplierLoggedIn: boolean;
  sampleAddEditAccess: boolean;
  bulkPriorityMode: boolean;
}

/** One Priority cell a fill or paste wrote. */
export interface PriorityWrite {
  sampleId: string;
  priority: string | null;
  previous: string | null;
}

/** The saves one batch of writes needs, and the writes no save can carry. */
export interface PriorityWritePlan {
  groups: Array<{ priority: string; sampleIds: string[] }>;
  refused: PriorityWrite[];
}

/**
 * Whether a row's Priority takes a fill or paste: the rule that enables its pill, minus bulk mode,
 * where a click captures a value instead of setting one.
 * @param row - the grid row
 * @param gates - the screen's rights and mode
 * @returns true when the cell may be written
 */
export function canFillPriority(row: Record<string, unknown> | undefined, gates: PriorityFillGates): boolean {
  return !gates.isSupplierLoggedIn && gates.sampleAddEditAccess && !gates.bulkPriorityMode && Boolean(row?.devOrderId);
}

/**
 * The stored priority a copied or pasted text stands for, matched without regard to case.
 * @param text - the text on the clipboard
 * @returns the priority as stored, or undefined when the text is none of them
 */
export function resolvePriorityText(text: unknown): string | undefined {
  const wanted = String(text ?? '')
    .trim()
    .toLowerCase();
  return PRIORITY_LABELS.find((label) => label.toLowerCase() === wanted);
}

/**
 * Whether a key on the Priority cell must be kept from AG Grid: with the cell editable for the
 * fill handle, Enter, F2 and typing would open AG Grid's text editor over the pill, and Delete
 * would blank a value the pill cannot blank. Arrow keys and clipboard chords still reach the grid.
 * @param event - the keydown event
 * @returns true when AG Grid must not handle the key
 */
export function isPriorityCellEditingKey(event: KeyboardEvent): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  const { key } = event;
  return EDITOR_KEYS.has(key) || key.length === 1;
}

/**
 * Groups one batch of writes by the priority they set, one save per distinct value, and sets
 * aside the writes that carry no stored priority so their cells can be put back.
 * @param writes - the cells a drag or paste wrote
 * @returns the saves to send and the writes to undo
 */
export function planPriorityWrites(writes: PriorityWrite[]): PriorityWritePlan {
  const groups = new Map<string, string[]>();
  const refused: PriorityWrite[] = [];
  writes.forEach((write) => {
    const priority = resolvePriorityText(write.priority);
    if (!priority) {
      refused.push(write);
      return;
    }
    groups.set(priority, [...(groups.get(priority) ?? []), write.sampleId]);
  });
  return { groups: [...groups].map(([priority, sampleIds]) => ({ priority, sampleIds })), refused };
}
